import {Game} from "../../Game";
import {ReelModel} from "../ReelModel";
import {gsap} from "gsap";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class ReelsView extends PIXI.Container {
    constructor() {
        super();
        this.name = "ReelsView";

        this.init();
    }

    private _symbolContainer: PIXI.Container;

    get symbolContainer(): PIXI.Container {
        return this._symbolContainer;
    }

    private _reelModels: ReelModel[];

    get reelModels(): ReelModel[] {
        return this._reelModels;
    }

    public doGridExpansion(extraReels: number, extraRows: number, addReelsToRight: boolean, addRowsInBottom: boolean, xOffset: number, yOffset: number, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // #130
        if (this._reelModels.length >= Game.TOTAL_REELS_NUM) {
            addReelsToRight = false;
            extraReels = 0;
        }
        if (this._reelModels[0].symbols.length >= Game.TOTAL_REELS_NUM) {
            addRowsInBottom = false;
            extraRows = 0;
        }

        if (addReelsToRight) {
            for (let reelId: number = Game.ACTIVE_REELS_NUM; reelId < extraReels + Game.ACTIVE_REELS_NUM; reelId++) {
                this._reelModels.push(new ReelModel(reelId));

                this._reelModels[reelId].increaseRows(0, extraRows, true, tl, duration);
            }

            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                this._reelModels[reelId].increaseRows((addRowsInBottom ? Game.ACTIVE_ROWS_NUM : 0), extraRows, addRowsInBottom, tl, duration);
            }
        } else {
            if (extraReels) {
                for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                    this._reelModels[reelId].id = (extraReels + reelId);
                }
                for (let reelId: number = extraReels - 1; reelId > -1; reelId--) {
                    this._reelModels.unshift(new ReelModel(reelId));

                    this._reelModels[0].increaseRows(0, extraRows, true, tl, duration);
                }
            }

            for (let reelId: number = extraReels; reelId < Game.ACTIVE_REELS_NUM + extraReels; reelId++) {
                this._reelModels[reelId].increaseRows(Game.ACTIVE_ROWS_NUM, extraRows, addRowsInBottom, tl, duration);
            }
        }

        tl.to(this._symbolContainer, {
            duration,
            x: (Game.REEL_AREA_X - xOffset),
            y: (Game.REEL_AREA_Y - yOffset)
        }, "<");

        tl.add(() => {
            this.createReelViewMask();
        });

        return tl;
    }

    public resetGrid(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this._symbolContainer, {
            duration,
            x: (Game.REEL_AREA_X),
            y: (Game.REEL_AREA_Y)
        });

        tl.add(() => {
            this.createReelViewMask();
        });

        return tl;
    }

    private createReelViewMask(): void {
        if (this.getChildByName("REEL_VIEW_MASK")) {
            this.removeChild(this.getChildByName("REEL_VIEW_MASK"));
        }

        const reelMask: PIXI.Graphics = new PIXI.Graphics();
        reelMask.name = "REEL_VIEW_MASK";

        this._symbolContainer.mask = reelMask;

        const [xPos, yPos]: number[] = this.getReelPositions(Game.gridSize);

        reelMask.position.set(xPos, yPos)
        reelMask.beginFill();

        reelMask
            .lineTo(Game.SYMBOL_WIDTH, 0)
            .lineTo(Game.SYMBOL_WIDTH, -Game.SYMBOL_HEIGHT)
            .lineTo(Game.SYMBOL_WIDTH, -Game.SYMBOL_HEIGHT)
            .lineTo(((Game.ACTIVE_REELS_NUM - 1) * Game.SYMBOL_WIDTH), -Game.SYMBOL_HEIGHT)
            .lineTo(((Game.ACTIVE_REELS_NUM - 1) * Game.SYMBOL_WIDTH), 0)
            .lineTo((Game.ACTIVE_REELS_NUM * Game.SYMBOL_WIDTH), 0)
            .lineTo((Game.ACTIVE_REELS_NUM * Game.SYMBOL_WIDTH), ((Game.ACTIVE_ROWS_NUM - 2) * Game.SYMBOL_HEIGHT))
            .lineTo(((Game.ACTIVE_REELS_NUM - 1) * Game.SYMBOL_WIDTH), ((Game.ACTIVE_ROWS_NUM - 2) * Game.SYMBOL_HEIGHT))
            .lineTo(((Game.ACTIVE_REELS_NUM - 1) * Game.SYMBOL_WIDTH), ((Game.ACTIVE_ROWS_NUM - 1) * Game.SYMBOL_HEIGHT))
            .lineTo(Game.SYMBOL_WIDTH, ((Game.ACTIVE_ROWS_NUM - 1) * Game.SYMBOL_HEIGHT))
            .lineTo(Game.SYMBOL_WIDTH, ((Game.ACTIVE_ROWS_NUM - 2) * Game.SYMBOL_HEIGHT))
            .lineTo(0, ((Game.ACTIVE_ROWS_NUM - 2) * Game.SYMBOL_HEIGHT))
            .lineTo(0, 0)
            .lineTo(Game.SYMBOL_WIDTH, 0);

        reelMask.endFill();

        this.addChild(reelMask);
    }

    private getReelPositions(gridName: string): number[] {
        if (NolimitApplication.isLandscape) {
            switch (gridName) {
                case "8by6":
                    return [10, 140]
                case "6by8":
                    return [10, 140]
                case "8by8":
                    return [10, 140]
                case "9by8":
                    return [10, 130]
                case "8by9":
                    return [10, 130]
                case "9by9":
                    return [10, 130]
                default:
                    return [35, 185]
            }
        } else {
            switch (gridName) {
                case "8by6":
                    return [10, 140]
                case "6by8":
                    return [10, 140]
                case "8by8":
                    return [10, 130]
                case "9by8":
                    return [10, 130]
                case "8by9":
                    return [10, 130]
                case "9by9":
                    return [10, 130]
                default:
                    return [35, 185]
            }
        }
    }


    private init(): void {
        this._symbolContainer = new PIXI.Container();
        this._symbolContainer.position.set(Game.REEL_AREA_X, Game.REEL_AREA_Y);

        const reelsData: string[][] = Game.model.isRestore ? Game.model.initData.reelsAfterAvalanche : Game.model.data.reels;
        let xOffset: number = Game.hasGridExpanded ? (Game.scaleFactor === 0.8 ? 35 : 41) : 35,
            yOffset: number = Game.hasGridExpanded ? (Game.scaleFactor === 0.8 ? 35 : 40) : 85;

        this.createReelViewMask();

        if (Game.model.isRestore && Game.hasGridExpanded) {
            this._symbolContainer.position.set(Game.REEL_AREA_X - xOffset, Game.REEL_AREA_Y - yOffset);
        }

        this.addChild(this._symbolContainer);

        this._reelModels = [];
        for (let i: number = 0; i < Game.ACTIVE_REELS_NUM; i++) {
            const reel: ReelModel = new ReelModel(i);
            reel.init(reelsData[i].concat(), this._symbolContainer, Game.monsterReelsView.monsterSymbolContainer);
            this._reelModels.push(reel);
        }
    }
}